/**
 * Users Domain Hooks
 *
 * React Query hooks for user management using ts-rest.
 * Includes both query hooks (for fetching) and mutation hooks (for CUD operations).
 */

import { usersQueryClient } from '@/lib/api/ts-rest-client';
import { queryKeys } from '@/lib/query-client';
import type {
  AddMobileAccessBody,
  AssignRoleBody,
  CreateUserBody,
  DeactivateUserBody,
  ListUsersQuery,
  ResetPasswordBody,
  UpdateAddressBody,
  UpdateUserBody,
} from '@skewb-works/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Query Options
// ---------------------------------------------------------------------------

const userQueryOptions = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  retry: 1,
  retryDelay: 2000,
};

// ---------------------------------------------------------------------------
// Query Hooks (data fetching)
// ---------------------------------------------------------------------------

/**
 * Fetch paginated user list with filters
 */
export function useUsers(filters?: Partial<ListUsersQuery>, enabled = true) {
  const queryKey = queryKeys.users.list(filters as Record<string, unknown>);
  const queryParams = filters || {};

  const query = usersQueryClient.listUsers.useQuery(
    queryKey,
    { query: queryParams as ListUsersQuery },
    { ...userQueryOptions, queryKey, enabled },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Fetch single user detail
 */
export function useUser(userId: string) {
  const queryKey = queryKeys.users.detail(userId);

  const query = usersQueryClient.getUserDetail.useQuery(
    queryKey,
    { params: { userId } },
    {
      ...userQueryOptions,
      queryKey,
      enabled: !!userId,
    },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Fetch aggregate user counts
 */
export function useUserCounts() {
  const queryKey = queryKeys.users.counts();

  const query = usersQueryClient.getUserCounts.useQuery(
    queryKey,
    {},
    { ...userQueryOptions, queryKey },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Fetch verification statistics
 */
export function useVerificationStats() {
  const queryKey = queryKeys.users.verificationStats();

  const query = usersQueryClient.getVerificationStats.useQuery(
    queryKey,
    {},
    { ...userQueryOptions, queryKey },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Fetch job titles for dropdowns
 */
export function useJobTitles(search?: string) {
  const queryKey = [...queryKeys.users.all, 'jobTitles', search] as const;

  const query = usersQueryClient.listJobTitles.useQuery(
    queryKey,
    { query: { search } },
    { ...userQueryOptions, queryKey },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Fetch disciplines for dropdowns
 */
export function useDisciplines() {
  const queryKey = [...queryKeys.users.all, 'disciplines'] as const;

  const query = usersQueryClient.listDisciplines.useQuery(
    queryKey,
    {},
    { ...userQueryOptions, queryKey },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Fetch line managers for dropdowns
 */
export function useLineManagers(search?: string) {
  const queryKey = queryKeys.users.lineManagers(search);

  const query = usersQueryClient.listLineManagers.useQuery(
    queryKey,
    { query: { search } },
    { ...userQueryOptions, queryKey },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Fetch user roles and permissions
 */
export function useUserRoles(userId: string) {
  const queryKey = queryKeys.users.roles(userId);

  const query = usersQueryClient.getUserRoles.useQuery(
    queryKey,
    { params: { userId } },
    {
      ...userQueryOptions,
      queryKey,
      enabled: !!userId,
    },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Fetch a user's mobile application access / entitlements
 */
export function useMobileAccess(userId: string) {
  const queryKey = queryKeys.users.mobileAccess(userId);

  const query = usersQueryClient.getMobileAccess.useQuery(
    queryKey,
    { params: { userId } },
    {
      ...userQueryOptions,
      queryKey,
      enabled: !!userId,
    },
  );

  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

// ---------------------------------------------------------------------------
// Mutation Hooks (create, update, delete)
// ---------------------------------------------------------------------------

/**
 * Create a new user
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserBody) => {
      const result = await usersQueryClient.createUser.mutation({ body: data });
      if (result.status !== 201) {
        const message = (result.body as any)?.error?.message || 'Failed to create user';
        throw new Error(message);
      }
      return result.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.counts() });
    },
    meta: {
      resourceName: 'User',
      action: 'create',
      errorTitle: 'Failed to Create User',
    },
  });
}

/**
 * Whether SSO is enabled for this client, i.e. whether the provisioning dialog
 * should offer an "SSO login?" choice. Defaults to false while loading or on error
 * so the choice is never shown speculatively.
 */
export function useMobileSsoAvailability() {
  const queryKey = queryKeys.users.mobileSsoAvailability();
  const query = usersQueryClient.getMobileSsoAvailability.useQuery(
    queryKey,
    {},
    {
      ...userQueryOptions,
      queryKey,
    },
  );
  return query.data?.status === 200 ? query.data.body.ssoAvailable : false;
}

/**
 * Grant the default mobile application access to an existing user.
 */
export function useAddMobileAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: AddMobileAccessBody }) => {
      const result = await usersQueryClient.addMobileAccess.mutation({
        params: { userId },
        body: data,
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to add mobile access');
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.mobileAccess(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      successTitle: 'Mobile Access Granted',
      successMessage: 'The user now has access to the mobile application',
      errorTitle: 'Failed to Grant Mobile Access',
    },
  });
}

/**
 * Update user details
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateUserBody }) => {
      const result = await usersQueryClient.updateUser.mutation({
        params: { userId },
        body: data,
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to update user');
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
    meta: {
      resourceName: 'User',
      action: 'update',
    },
  });
}

/**
 * Update user address
 */
export function useUpdateAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateAddressBody }) => {
      const result = await usersQueryClient.updateAddress.mutation({
        params: { userId },
        body: data,
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to update address');
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
    },
    meta: {
      resourceName: 'Address',
      action: 'update',
    },
  });
}

/**
 * Deactivate a user
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: DeactivateUserBody }) => {
      const result = await usersQueryClient.deactivateUser.mutation({
        params: { userId },
        body: data,
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to deactivate user');
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.counts() });
    },
    meta: {
      successTitle: 'User Deactivated',
      successMessage: 'The user has been deactivated successfully',
    },
  });
}

/**
 * Reactivate a user
 */
export function useReactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const result = await usersQueryClient.reactivateUser.mutation({
        params: { userId },
        body: {},
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to reactivate user');
      }
      return result.body;
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.counts() });
    },
    meta: {
      successTitle: 'User Reactivated',
      successMessage: 'The user has been reactivated successfully',
    },
  });
}

/**
 * Reset another user's password.
 *
 * Default mode (`email`) calls Cognito's AdminResetUserPasswordCommand and
 * the user receives a temporary password via email. The `direct` mode is
 * dev-only and sets the password without email — the backend rejects it in
 * non-development environments.
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: ResetPasswordBody }) => {
      const result = await usersQueryClient.resetPassword.mutation({
        params: { userId },
        body: data,
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to reset password');
      }
      return result.body;
    },
    meta: {
      successTitle: 'Reset email sent',
      successMessage:
        'The user has been emailed instructions. They must use the verification code (or temporary password, for new users) to set a new password.',
    },
  });
}

/**
 * Assign role to user
 */
export function useAssignRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: AssignRoleBody }) => {
      const result = await usersQueryClient.assignRole.mutation({
        params: { userId },
        body: data,
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to assign role');
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.roles(variables.userId) });
    },
    meta: {
      successTitle: 'Role Assigned',
      successMessage: 'The role has been assigned successfully',
    },
  });
}

/**
 * Remove role from user
 */
export function useRemoveRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, groupName }: { userId: string; groupName: string }) => {
      const result = await usersQueryClient.removeRole.mutation({
        params: { userId, groupName },
        body: {},
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to remove role');
      }
      return result.body;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.roles(variables.userId) });
    },
    meta: {
      successTitle: 'Role Removed',
      successMessage: 'The role has been removed successfully',
    },
  });
}
